import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const PRICING_MODELS = [
  { value: "per_lead_fixed", label: "Per Lead - Fixed" },
  { value: "per_lead_variable", label: "Per Lead - Variable" },
  { value: "monthly_retainer", label: "Monthly Retainer" },
];

const SPEND_TYPES = [
  { value: "retainer", label: "Retainer" },
  { value: "ad_spend", label: "Ad Spend" },
  { value: "platform_fee", label: "Platform Fee" },
  { value: "credit", label: "Credit" },
  { value: "other", label: "Other" },
];

export const AcquisitionCommandPage = () => {
  const navigate = useNavigate();
  const { user, rolePermissions } = useAuth();
  const [sources, setSources] = useState([]);
  const [spendRows, setSpendRows] = useState([]);
  const [intelRows, setIntelRows] = useState([]);
  const [intelRange, setIntelRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
    return { start, end };
  });
  const [loading, setLoading] = useState(false);
  const [spendLoading, setSpendLoading] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceForm, setSourceForm] = useState({
    name: "",
    pricing_model: "per_lead_variable",
    fixed_cost_per_lead: "",
    monthly_retainer_amount: "",
    is_active: true,
  });

  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [ledgerForm, setLedgerForm] = useState({
    ledger_id: null,
    lead_source_id: "",
    amount: "",
    spend_date: "",
    spend_type: "ad_spend",
    notes: "",
  });
  const [range, setRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
    return { start, end };
  });

  const isAllowed =
    user &&
    (rolePermissions?.[user.role]?.view_acquisition_command ||
      ["owner", "admin", "finance"].includes(user.role));

  const enabledLeadSources = useMemo(
    () => sources.filter((s) => s.is_active),
    [sources]
  );


  useEffect(() => {
    if (!isAllowed) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await API.get("/lead-sources/", { params: { active: false } });
        setSources(Array.isArray(res.data) ? res.data : []);
        await Promise.all([loadSpend(), loadIntel()]);
      } catch (e) {
        toast.error("Failed to load lead sources");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const loadIntel = async () => {
    if (!isAllowed) return;
    try {
      const startIso = `${intelRange.start}T00:00:00`;
      const endIso = `${intelRange.end}T23:59:59`;
      const res = await API.get("/acquisition/acquisition-intel", {
        params: { start: startIso, end: endIso },
      });
      setIntelRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // snapshot is read-only; silent failure is acceptable here
    }
  };

  const loadSpend = async () => {
    if (!isAllowed) return;
    setSpendLoading(true);
    try {
      const startIso = `${range.start}T00:00:00`;
      const endIso = `${range.end}T23:59:59`;
      const res = await API.get("/acquisition/lead-spend", {
        params: { start: startIso, end: endIso },
      });
      setSpendRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to load spend ledger");
    } finally {
      setSpendLoading(false);
    }
  };

  useEffect(() => {
    loadSpend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  // Enable a catalog entry for this company (creates a company lead source)
  const enableCatalogSource = async (entry) => {
    if (!entry?.catalog_id) {
      toast.error("Catalog item missing catalog_id");
      return;
    }

    try {
      await API.post("/lead-sources/", {
        name: entry.display_name || entry.source_name,
        // Store catalog linkage + metadata so UI can show lead type/platform/category
        catalog_id: entry.catalog_id,
        lead_type: entry.lead_type || entry.source_name,
        platform: entry.platform || null,
        category: entry.category || null,

        // Defaults (editable later). Use per_lead_variable so no amount is required.
        pricing_model: "per_lead_variable",
        fixed_cost_per_lead: null,
        monthly_retainer_amount: null,
        is_active: true,
      });

      toast.success("Enabled lead source");
      const res = await API.get("/lead-sources/", { params: { active: false } });
      setSources(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to enable lead source";
      toast.error(msg);
    }
  };

  const openNewSource = () => {
    setEditingSource({}); // opens dialog (because !!editingSource === true)
    setSourceForm({
      name: "",
      pricing_model: "per_lead_variable",
      fixed_cost_per_lead: "",
      monthly_retainer_amount: "",
      is_active: true,

      // optional fields if your form supports them
      catalog_id: "",
      lead_type: "",
      platform: "",
      category: "",
    });
  };

  const handleEditSource = (s) => {
    setEditingSource(s);
    setSourceForm({
      name: s.name || "",
      pricing_model: s.pricing_model || "per_lead_fixed",
      fixed_cost_per_lead: s.fixed_cost_per_lead != null ? String(s.fixed_cost_per_lead) : "",
      monthly_retainer_amount:
        s.monthly_retainer_amount != null ? String(s.monthly_retainer_amount) : "",
      is_active: !!s.is_active,
      catalog_id: s.catalog_id || "",
      lead_type: s.lead_type || "",
      platform: s.platform || "",
      category: s.category || "",
    });
  };

  const handleToggleActive = async (s) => {
    try {
      await API.put(`/lead-sources/${s.lead_source_id}`, { is_active: !s.is_active });
      toast.success("Lead source updated");
      const res = await API.get("/lead-sources/", { params: { active: false } });
      setSources(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to update source");
    }
  };

  const openNewSourceFromLibrary = (entry) => {
    // Keep this helper if you later want a pre-filled dialog flow; for now
    // the primary enable path uses enableCatalogSource above.
    setEditingSource({});
    setSourceForm({
      name: entry.display_name || "",
      pricing_model: "per_lead_fixed",
      fixed_cost_per_lead: "",
      monthly_retainer_amount: "",
      is_active: true,
      catalog_id: entry.catalog_id || "",
      platform: entry.platform || "",
      category: entry.category || "",
      lead_type: entry.lead_type || "",
    });
  };

  const saveSource = async () => {
    const toNumOrNull = (v) => {
      if (v == null) return null;
      const s = String(v).trim();
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    };

    const payload = {
      name: (sourceForm.name || "").trim(),
      pricing_model: sourceForm.pricing_model,
      fixed_cost_per_lead: toNumOrNull(sourceForm.fixed_cost_per_lead),
      monthly_retainer_amount: toNumOrNull(sourceForm.monthly_retainer_amount),
      is_active: !!sourceForm.is_active,

      // optional metadata
      catalog_id: (sourceForm.catalog_id || "").trim() || null,
      lead_type: (sourceForm.lead_type || "").trim() || null,
      platform: (sourceForm.platform || "").trim() || null,
      category: (sourceForm.category || "").trim() || null,
    };

    // hard guards (prevents generic backend error)
    if (!payload.name) {
      toast.error("Name is required");
      return;
    }
    if (payload.pricing_model === "per_lead_fixed" && payload.fixed_cost_per_lead == null) {
      toast.error("Fixed CPL is required for Per Lead – Fixed");
      return;
    }
    if (payload.pricing_model === "monthly_retainer" && payload.monthly_retainer_amount == null) {
      toast.error("Monthly retainer amount is required for Monthly Retainer");
      return;
    }

    try {
      if (editingSource?.lead_source_id) {
        await API.put(`/lead-sources/${editingSource.lead_source_id}`, payload);
      } else {
        await API.post("/lead-sources/", payload);
      }
      toast.success("Lead source saved");
      const res = await API.get("/lead-sources/", { params: { active: false } });
      setSources(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to save lead source");
    }
  };

  const openLedger = (row) => {
    if (row) {
      setLedgerForm({
        ledger_id: row.ledger_id,
        lead_source_id: row.lead_source_id,
        amount: String(row.amount ?? ""),
        spend_date: row.spend_date?.slice(0, 10) || "",
        spend_type: row.spend_type || "ad_spend",
        notes: row.notes || "",
      });
    } else {
      setLedgerForm({
        ledger_id: null,
        lead_source_id: sources[0]?.lead_source_id || "",
        amount: "",
        spend_date: range.start,
        spend_type: "ad_spend",
        notes: "",
      });
    }
    setLedgerDialogOpen(true);
  };

  const saveLedger = async () => {
    if (!ledgerForm.lead_source_id || !ledgerForm.amount || !ledgerForm.spend_date) {
      toast.error("Source, amount, and date are required");
      return;
    }
    try {
      const payload = {
        lead_source_id: ledgerForm.lead_source_id,
        amount: parseFloat(ledgerForm.amount),
        spend_date: `${ledgerForm.spend_date}T00:00:00`,
        spend_type: ledgerForm.spend_type,
        notes: ledgerForm.notes || null,
      };
      if (ledgerForm.ledger_id) {
        await API.put(`/acquisition/lead-spend/${ledgerForm.ledger_id}`, payload);
      } else {
        await API.post("/acquisition/lead-spend", payload);
      }
      toast.success("Spend entry saved");
      setLedgerDialogOpen(false);
      loadSpend();
    } catch (e) {
      toast.error("Failed to save spend entry");
    }
  };

  const deleteLedger = async (row) => {
    if (!window.confirm("Delete this spend entry?")) return;
    try {
      await API.delete(`/acquisition/lead-spend/${row.ledger_id}`);
      toast.success("Spend entry deleted");
      loadSpend();
    } catch (e) {
      toast.error("Failed to delete spend entry");
    }
  };

  if (!isAllowed) {
    return (
      <div className="w-full space-y-6">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-100">Acquisition Command</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-400">
              You do not have permission to access Acquisition Command. Contact an owner or admin to request access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-100"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="text-xs text-slate-400">Acquisition Command</div>
        </div>
      </div>

      {/* CARD 1 — LEAD SOURCE LIBRARY */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex items-center justify-between">
          <div>
            <CardTitle className="text-slate-100">Lead Source Library</CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Standardized catalog of acquisition sources. Select and enable for this company.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <LeadSourceLibrary enabledSources={sources} onEnable={enableCatalogSource} />
        </CardContent>
      </Card>

      {/* CARD 2 — ENABLED COMPANY SOURCES */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex items-center justify-between">
          <div>
            <CardTitle className="text-slate-100">Enabled Lead Sources</CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Active acquisition loadout. Only these sources appear in Create Lead.
            </p>
          </div>
          <Button size="sm" onClick={openNewSource} className="bg-emerald-600 hover:bg-emerald-500 text-white">
            New Source
          </Button>
        </CardHeader>
        <CardContent>
          <EnabledLeadSourcesTable
            sources={sources}
            onEditSource={handleEditSource}
            onToggleActive={handleToggleActive}
          />
        </CardContent>
      </Card>

      {/* CARD 3 — ACQUISITION COMMAND SNAPSHOT */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex items-center justify-between">
          <div>
            <CardTitle className="text-slate-100">Acquisition Command Snapshot</CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Mission-grade view of what your marketing dollars are doing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-400">From</Label>
            <Input
              type="date"
              className="h-8 w-36 bg-slate-950 border-slate-700 text-slate-100"
              value={intelRange.start}
              onChange={(e) => setIntelRange((r) => ({ ...r, start: e.target.value }))}
            />
            <Label className="text-xs text-slate-400">To</Label>
            <Input
              type="date"
              className="h-8 w-36 bg-slate-950 border-slate-700 text-slate-100"
              value={intelRange.end}
              onChange={(e) => setIntelRange((r) => ({ ...r, end: e.target.value }))}
            />
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-500 text-emerald-400"
              onClick={loadIntel}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-4">
            CPL is your cost per lead. CPA is your cost per win. Push budget to the sources with the lowest CPA and
            strongest conversion.
          </p>
          <AcquisitionSnapshotTable intelRows={intelRows} sources={sources} />
        </CardContent>
      </Card>

      {/* Existing Spend Ledger card (kept as supporting detail) */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="text-slate-100">Spend Ledger</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              className="h-8 w-40 bg-slate-950 border-slate-700 text-slate-100"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            />
            <span className="text-slate-500 text-xs">to</span>
            <Input
              type="date"
              className="h-8 w-40 bg-slate-950 border-slate-700 text-slate-100"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            />
            <Button size="sm" variant="outline" onClick={loadSpend} disabled={spendLoading}>
              Refresh
            </Button>
            <Button size="sm" onClick={() => openLedger(null)} className="bg-emerald-600 hover:bg-emerald-500 text-white">
              New Entry
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border border-slate-800 rounded-md bg-slate-950/60">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Notes</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {spendRows.map((row) => {
                  const src = sources.find((s) => s.lead_source_id === row.lead_source_id);
                  return (
                    <tr key={row.ledger_id} className="border-t border-slate-800">
                      <td className="px-3 py-2 text-slate-100">
                        {row.spend_date ? row.spend_date.slice(0, 10) : ""}
                      </td>
                      <td className="px-3 py-2 text-slate-300">{src?.name || row.lead_source_id}</td>
                      <td className="px-3 py-2 text-right text-slate-100">
                        ${row.amount?.toFixed ? row.amount.toFixed(2) : row.amount}
                      </td>
                      <td className="px-3 py-2 text-slate-300">{row.spend_type}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-xs truncate">{row.notes}</td>
                      <td className="px-3 py-2 text-right space-x-2">
                        <Button size="xs" variant="outline" onClick={() => openLedger(row)}>
                          Edit
                        </Button>
                        <Button size="xs" variant="outline" onClick={() => deleteLedger(row)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {spendRows.length === 0 && !spendLoading && (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-500" colSpan={6}>
                      No spend entries in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Source Edit Dialog */}
      <Dialog open={!!editingSource} onOpenChange={() => setEditingSource(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>
              {editingSource?.lead_source_id ? "Edit Lead Source" : "New Lead Source"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400">Name</Label>
              <Input
                className="bg-slate-950 border-slate-700 text-slate-100"
                value={sourceForm.name}
                onChange={(e) => setSourceForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Pricing Model</Label>
              <Select
                value={sourceForm.pricing_model}
                onValueChange={(v) => setSourceForm((f) => ({ ...f, pricing_model: v }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {PRICING_MODELS.map((pm) => (
                    <SelectItem key={pm.value} value={pm.value}>
                      {pm.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {sourceForm.pricing_model === "per_lead_fixed" && (
              <div>
                <Label className="text-xs text-slate-400">Fixed Cost Per Lead</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="bg-slate-950 border-slate-700 text-slate-100"
                  value={sourceForm.fixed_cost_per_lead}
                  onChange={(e) => setSourceForm((f) => ({ ...f, fixed_cost_per_lead: e.target.value }))}
                />
              </div>
            )}
            {sourceForm.pricing_model === "monthly_retainer" && (
              <div>
                <Label className="text-xs text-slate-400">Monthly Retainer Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="bg-slate-950 border-slate-700 text-slate-100"
                  value={sourceForm.monthly_retainer_amount}
                  onChange={(e) => setSourceForm((f) => ({ ...f, monthly_retainer_amount: e.target.value }))}
                />
              </div>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={sourceForm.is_active}
                onChange={(e) => setSourceForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded border-slate-700"
              />
              <Label htmlFor="is_active" className="text-xs text-slate-400">
                Active
              </Label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setEditingSource(null)}>
                Cancel
              </Button>
              <Button onClick={saveSource} className="bg-emerald-600 hover:bg-emerald-500">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog */}
      <Dialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>
              {ledgerForm.ledger_id ? "Edit Spend Entry" : "New Spend Entry"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400">Lead Source</Label>
              <Select
                value={ledgerForm.lead_source_id}
                onValueChange={(v) => setLedgerForm((f) => ({ ...f, lead_source_id: v }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {sources.map((s) => (
                    <SelectItem key={s.lead_source_id} value={s.lead_source_id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount</Label>
              <Input
                type="number"
                step="0.01"
                className="bg-slate-950 border-slate-700 text-slate-100"
                value={ledgerForm.amount}
                onChange={(e) => setLedgerForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Spend Date</Label>
              <Input
                type="date"
                className="bg-slate-950 border-slate-700 text-slate-100"
                value={ledgerForm.spend_date}
                onChange={(e) => setLedgerForm((f) => ({ ...f, spend_date: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Spend Type</Label>
              <Select
                value={ledgerForm.spend_type}
                onValueChange={(v) => setLedgerForm((f) => ({ ...f, spend_type: v }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {SPEND_TYPES.map((st) => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Notes</Label>
              <Textarea
                className="bg-slate-950 border-slate-700 text-slate-100"
                value={ledgerForm.notes}
                onChange={(e) => setLedgerForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setLedgerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveLedger} className="bg-emerald-600 hover:bg-emerald-500">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeadSourceLibrary = ({ enabledSources, onEnable }) => {
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get("/lead-sources/catalog");
        setCatalog(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        // silent
      }
    };
    load();
  }, []);

  const enabledIds = new Set((enabledSources || []).map((s) => s.catalog_id).filter(Boolean));

  const filtered = catalog.filter((c) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (c.display_name || "").toLowerCase().includes(term) ||
      (c.platform || "").toLowerCase().includes(term) ||
      (c.lead_type || "").toLowerCase().includes(term)
    );
  });

  const handleEnableSource = async () => {
    if (!selected) return;
    const entry = catalog.find((c) => c.catalog_id === selected);
    if (!entry) return;
    if (enabledIds.has(entry.catalog_id)) {
      toast.error("Source already active");
      return;
    }
    try {
      setSubmitting(true);
      if (onEnable) {
        await onEnable(entry);
      }
      setSelected("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3">
        <div className="flex-1 space-y-1">
          <Label className="text-xs text-slate-400">Lead Source Library</Label>
          <Input
            placeholder="Search catalog (Google, Facebook, Remodel Favor...)"
            className="h-8 bg-slate-950 border-slate-700 text-slate-100 text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex-1">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100 h-9 text-xs">
              <SelectValue placeholder="Select a lead source to enable" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-72">
              {filtered.map((c) => (
                <SelectItem key={c.catalog_id} value={c.catalog_id} disabled={enabledIds.has(c.catalog_id)}>
                  <div className="flex flex-col">
                    <span>{c.display_name}</span>
                    <span className="text-xs text-slate-400">
                      {c.platform} • {c.category} • {c.lead_type}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          disabled={!selected || enabledIds.has(selected) || submitting}
          onClick={handleEnableSource}
          isLoading={submitting}
        >
          Enable for Company
        </Button>
      </div>
    </div>
  );
};

const EnabledLeadSourcesTable = ({ sources, onEditSource, onToggleActive }) => {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border border-slate-800 bg-slate-950/60">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-900 text-slate-400 uppercase tracking-wide text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Lead Type</th>
              <th className="px-3 py-2 text-left">Platform</th>
              <th className="px-3 py-2 text-left">Pricing Model</th>
              <th className="px-3 py-2 text-left">Fixed CPL</th>
              <th className="px-3 py-2 text-left">Monthly Retainer</th>
              <th className="px-3 py-2 text-left">Active Range</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.lead_source_id} className="border-t border-slate-800">
                <td className="px-3 py-2 text-slate-100">{s.name}</td>
                <td className="px-3 py-2 text-slate-300">{s.lead_type || "—"}</td>
                <td className="px-3 py-2 text-slate-300">{s.platform || "—"}</td>
                <td className="px-3 py-2 text-slate-200">{s.pricing_model}</td>
                <td className="px-3 py-2 text-slate-200">
                  {s.fixed_cost_per_lead != null ? `$${s.fixed_cost_per_lead.toFixed(2)}` : "—"}
                </td>
                <td className="px-3 py-2 text-slate-200">
                  {s.monthly_retainer_amount != null ? `$${s.monthly_retainer_amount.toFixed(2)}` : "—"}
                </td>
                <td className="px-3 py-2 text-slate-300">
                  {s.active_start ? s.active_start.slice(0, 10) : "—"} {s.active_end ? `→ ${s.active_end.slice(0, 10)}` : ""}
                </td>
                <td className="px-3 py-2">
                  <span className={s.is_active ? "text-emerald-400" : "text-slate-500"}>
                    {s.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <Button
                    size="xs"
                    variant="outline"
                    className="border-slate-700 text-slate-100"
                    onClick={() => onEditSource(s)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    className={s.is_active ? "border-amber-500 text-amber-400" : "border-slate-700 text-slate-300"}
                    onClick={() => onToggleActive(s)}
                  >
                    {s.is_active ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={9}>
                  No lead sources enabled for this company.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AcquisitionSnapshotTable = ({ intelRows, sources }) => {
  const sourceById = useMemo(() => Object.fromEntries(sources.map((s) => [s.lead_source_id, s])), [sources]);

  return (
    <div className="overflow-x-auto rounded-md border border-slate-800 bg-slate-950/60">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-900 text-slate-400 uppercase tracking-wide text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-left">Pricing</th>
            <th className="px-3 py-2 text-right">Leads</th>
            <th className="px-3 py-2 text-right">Converted</th>
            <th className="px-3 py-2 text-right">Lost</th>
            <th className="px-3 py-2 text-right">Conv %</th>
            <th className="px-3 py-2 text-right">Spend</th>
            <th className="px-3 py-2 text-right">CPL</th>
            <th className="px-3 py-2 text-right">CPA</th>
          </tr>
        </thead>
        <tbody>
          {intelRows.map((row) => {
            const src = sourceById[row.lead_source_id] || {};
            const total = row.leads_total || 0;
            const conv = row.conversions_total || 0;
            const lost = total - conv;
            const convRate = total > 0 ? (conv / total) * 100 : 0;
            const spend = row.spend_total || 0;
            const cpl = row.cpl_blended ?? (total > 0 ? spend / total : null);
            const cpa = row.cpa ?? (conv > 0 ? spend / conv : null);
            return (
              <tr key={row.lead_source_id} className="border-t border-slate-800">
                <td className="px-3 py-2 text-slate-100">
                  <div className="flex flex-col">
                    <span>{src.name || row.name || row.lead_source_id}</span>
                    <span className="text-xs text-slate-500">{src.platform || "—"}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-300">{src.pricing_model || "—"}</td>
                <td className="px-3 py-2 text-right text-slate-100">{total}</td>
                <td className="px-3 py-2 text-right text-emerald-400">{conv}</td>
                <td className="px-3 py-2 text-right text-slate-500">{lost}</td>
                <td className="px-3 py-2 text-right text-slate-100">
                  {total > 0 ? `${convRate.toFixed(1)}%` : "—"}
                </td>
                <td className="px-3 py-2 text-right text-slate-100">{spend ? `$${spend.toFixed(0)}` : "—"}</td>
                <td className="px-3 py-2 text-right text-slate-100">
                  {cpl != null ? `$${cpl.toFixed(0)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right text-slate-100">
                  {cpa != null ? `$${cpa.toFixed(0)}` : "—"}
                </td>
              </tr>
            );
          })}
          {intelRows.length === 0 && (
            <tr>
              <td className="px-3 py-6 text-center text-slate-500" colSpan={9}>
                No acquisition activity in this range.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

 
